/** @prettier */

import {useNavigate, LoaderFunctionArgs} from 'react-router-dom';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';
import {Grid, Box, Container, Stack} from '@mui/material';

import {useState, useEffect} from 'react';
import {useLoaderData} from 'react-router-dom';
import ReactDiffViewer from 'react-diff-viewer-continued';

import {
  getCompositionsSitesBySiteID,
  getSettingUser,
} from '../../API/API_Collection';
import getLogo from '../../functions/getLogo';
import {useTranslation} from 'react-i18next';

import {routeList} from '../PrivateRoute';

import {file} from '../../define';

//MTH component import
import Footer from '../MainFooter';

type LoaderReturnType = {
  /** M-ACCESSのロゴデータ（取得できない場合は「default」が入る） */
  maccessLogo: string;
  /** MITSUBISHIのロゴデータ */
  mhiLogo: string;
  /** タイムゾーン */
  timeZone: apiIF.res.getCompositionsSitesBySiteID['timezone'];
  /** フッターテキスト */
  footerText: apiIF.res.getSettingUser['footer-text'];
  /** 言語切り替え */
  language: apiIF.res.getSettingUser['language'];
};

export async function loader({
  params,
}: LoaderFunctionArgs): Promise<LoaderReturnType | undefined> {
  //パスパラメータを取得できない場合
  if (params.siteID === undefined) {
    return undefined;
  }
  //パスパラメータを取得できない場合
  if (params.gwID === undefined) {
    return undefined;
  }

  try {
    let promises = [];
    //ロゴを取得
    let maccessLogo: LoaderReturnType['maccessLogo'] = '';
    let mhiLogo: LoaderReturnType['mhiLogo'] = '';
    promises.push(
      getLogo(
        logo => {
          maccessLogo = logo;
        },
        logo => {
          mhiLogo = logo;
        }
      )
    );

    //サイトの情報を取得
    promises.push(getCompositionsSitesBySiteID(params.siteID));
    //ユーザ情報を取得
    promises.push(getSettingUser());

    const response = await Promise.all(promises);

    const siteInfo = response[1] as apiIF.res.getCompositionsSitesBySiteID;
    const userSettingInfo = response[2] as apiIF.res.getSettingUser;

    return {
      maccessLogo: maccessLogo,
      mhiLogo: mhiLogo,
      timeZone: siteInfo['timezone'],
      footerText: userSettingInfo['footer-text'],
      language: userSettingInfo['language'],
    };
  } catch {
    return undefined;
  }
}

/**
 * 差分比較データの型の定義
 * @param displayOldData - GETAPIから取得した現在の設定
 * @param displayNewData - CSVファイルから取得した設定
 */
interface diffData {
  displayOldData: string;
  displayNewData: string;
}

/**
 * 空調機差分比較画面
 */
const CollectiveSettingDifference = () => {
  const loaderData = useLoaderData() as LoaderReturnType | undefined;
  const navigate = useNavigate();
  const [trans, i18n] = useTranslation(); //翻訳関数
  const [diffData, setDiffData] = useState<diffData>({
    displayOldData: '',
    displayNewData: '',
  });

  useEffect(() => {
    //言語切替の初期値設定
    if (loaderData === undefined) return;
    i18n.changeLanguage(loaderData.language);

    // セッションストレージからデータを読み出し
    const savedData = sessionStorage.getItem('diffData');
    if (savedData) {
      setDiffData(JSON.parse(savedData));
      sessionStorage.removeItem('diffData'); // データを読み出した後、セッションストレージから削除
    } else {
      // セッションストレージにデータが存在しない場合はmy-siteに飛ぶ
      // URL直打ちの場合もこの処理を実施する
      navigate(routeList.mySite.path);
    }
  }, [navigate]);

  if (loaderData === undefined) {
    return <></>;
  }

  return (
    <>
      <header>
        <Grid
          container
          columnSpacing={3}
          justifyContent="space-between"
          alignItems="center">
          {/* M-ACCESSのロゴ */}
          <Grid item xs="auto" className="logoBox">
            {loaderData.maccessLogo === 'default' ? (
              <img
                className="logoBox__icon--m-accessLogo"
                src={file.MACCESS_LOGO_PATH}
                alt={`${trans(
                  `CollectiveSettingDiff.image.
                  alternativeText.mAccessLogo`
                )}`}
                id="mv-mAccessLogo"
              />
            ) : loaderData.maccessLogo === '' ? (
              <Box className="logoBox__icon--m-accessLogo"></Box>
            ) : (
              <img
                className="logoBox__icon--m-accessLogo"
                src={loaderData.maccessLogo}
                alt={`${trans(
                  `CollectiveSettingDiff.image.alternativeText.changedMAccessLogo`
                )}`}
              />
            )}
          </Grid>
          {/* MHIのロゴ */}
          <Grid item xs="auto" className="logoBox">
            {loaderData.mhiLogo === 'default' ? (
              <img
                className="logoBox__icon--mhiLogo"
                src={file.MHI_LOGO_PATH}
                alt={`${trans(
                  `CollectiveSettingDiff.image.alternativeText.mhiLogo`
                )}`}
                id="mv-mhiLogo"
              />
            ) : loaderData.mhiLogo === '' ? (
              <Box className="logoBox__icon--mhiLogo"></Box>
            ) : (
              <img
                className="logoBox__icon--mhiLogo"
                src={loaderData.mhiLogo}
                alt={`${trans(
                  `CollectiveSettingDiff.image.alternativeText.changedMhiLogo`
                )}`}
              />
            )}
          </Grid>
        </Grid>
      </header>
      <main>
        <Container fixed>
          <Stack>
            <h1>{trans('CollectiveSettingDiff.title')}</h1>
            <Box className="collectiveSettingConfirmation__text">
              {trans('CollectiveSettingDiff.description.difference')}
            </Box>
            <ReactDiffViewer
              oldValue={diffData.displayOldData}
              newValue={diffData.displayNewData}
              splitView={true}
              leftTitle={String(trans('CollectiveSettingDiff.table.leftTitle'))}
              rightTitle={String(
                trans('CollectiveSettingDiff.table.rightTitle')
              )}
              disableWordDiff={true}
              hideLineNumbers={true}
              styles={{
                contentText: {
                  fontFamily: 'DINNextLTPro-Regular',
                },
              }}
            />
          </Stack>
        </Container>
      </main>
      <Footer footerText={loaderData.footerText} />
    </>
  );
};

export default CollectiveSettingDifference;
